import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Camera, CheckCircle2, LoaderCircle, Lock, UserRound } from "lucide-react";
import { getGalleryShareLink, registerGalleryShareAccess, verifyGallerySharePin } from "@/lib/galleryApi";

const initialForm = {
  pin: "",
  name: "",
  email: "",
  mobile: "+91 ",
  consentPrivacy: false,
  consentMarketing: false,
  consentFaceSearch: false,
};

export default function GalleryShareAccess() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [selfieFrames, setSelfieFrames] = useState([]);

  const shareLink = data?.shareLink;
  const event = data?.event;
  const requiresPin = Boolean(shareLink?.requirePin);
  const requiresRegistration = Boolean(shareLink?.requireRegistration);
  const ownPhotosMode = Boolean(shareLink?.ownPhotosMode || shareLink?.accessType === "own_photos");
  const albumLabel = useMemo(() => {
    if (!shareLink?.allowedAlbumIds?.length) return "All event albums";
    return `${shareLink.albums?.length || shareLink.allowedAlbumIds.length} selected album${(shareLink.albums?.length || shareLink.allowedAlbumIds.length) === 1 ? "" : "s"}`;
  }, [shareLink]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getGalleryShareLink(shareToken)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shareToken]);

  const openGallery = (result) => {
    if (!result?.token || !result?.event?.slug) {
      throw new Error("Gallery access token was not returned");
    }
    sessionStorage.setItem(`gallery:${result.event.slug}`, result.token);
    sessionStorage.setItem(`gallery-share-token:${result.event.slug}`, shareToken);
    sessionStorage.setItem(`gallery-event:${result.event.slug}`, JSON.stringify({
      ...(result.event || {}),
      ...(result.faceSearch ? { faceSearch: result.faceSearch } : {}),
    }));
    navigate(`/gallery/${result.event.slug}`, { replace: true });
  };

  const submit = async (eventSubmit) => {
    eventSubmit.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (requiresRegistration) {
        const result = await registerGalleryShareAccess(shareToken, { ...form, selfieFrames });
        openGallery(result);
      } else {
        const result = await verifyGallerySharePin(shareToken, form.pin);
        openGallery(result);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-[#F7F1E8] px-5 py-24 flex items-center justify-center">
      <div className="w-full max-w-5xl grid lg:grid-cols-[0.9fr_1.1fr] border border-[#C99A45]/25 bg-[#050505]">
        <div className="relative min-h-[360px] p-8 md:p-10 flex flex-col justify-between overflow-hidden">
          {event?.coverImageUrl && (
            <img src={event.coverImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
          <div className="relative">
            <div className="eyebrow text-[#C99A45]">Private Client Gallery</div>
            <h1 className="font-display text-5xl md:text-6xl leading-none mt-5">{event?.eventName || "Client Gallery"}</h1>
            <p className="text-[#F7F1E8]/60 mt-5 max-w-sm">
              Register to get your photos on your mobile and unlock your private Mangalfera Films collection.
            </p>
          </div>
          <div className="relative flex flex-wrap gap-3 text-xs text-[#F7F1E8]/65">
            <span className="border border-[#C99A45]/25 px-3 py-2">{shareLink?.accessType || "private"} access</span>
            <span className="border border-[#C99A45]/25 px-3 py-2">{albumLabel}</span>
          </div>
        </div>

        <div className="p-8 md:p-10">
          {loading ? (
            <div className="flex items-center gap-3 text-[#C99A45]"><LoaderCircle className="animate-spin" /> Loading secure access...</div>
          ) : error && !data ? (
            <div className="border border-red-500/30 text-red-200 p-5">{error}</div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <div className="eyebrow text-[#C99A45]">Register to Get Your Photos on Your Mobile</div>
                <h2 className="font-display text-4xl mt-3">Access Your Gallery</h2>
              </div>

              {requiresPin && (
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.22em] text-[#F7F1E8]/50 flex items-center gap-2"><Lock size={13} /> PIN</span>
                  <input
                    value={form.pin}
                    onChange={(e) => setForm({ ...form, pin: e.target.value })}
                    className="admin-input w-full mt-2"
                    placeholder="Enter gallery PIN"
                    type="password"
                    required
                  />
                </label>
              )}

              {requiresRegistration && (
                <>
                  <label className="block">
                    <span className="text-xs uppercase tracking-[0.22em] text-[#F7F1E8]/50 flex items-center gap-2"><UserRound size={13} /> Name</span>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="admin-input w-full mt-2" required />
                  </label>
                  <div className="grid md:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="text-xs uppercase tracking-[0.22em] text-[#F7F1E8]/50">Email ID</span>
                      <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="admin-input w-full mt-2" type="email" />
                    </label>
                    <label className="block">
                      <span className="text-xs uppercase tracking-[0.22em] text-[#F7F1E8]/50">Mobile number</span>
                      <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="admin-input w-full mt-2" required />
                    </label>
                  </div>

                  {ownPhotosMode && (
                    <GuidedSelfieCapture
                      frames={selfieFrames}
                      onFrames={setSelfieFrames}
                    />
                  )}

                  <label className="flex items-start gap-3 text-sm text-[#F7F1E8]/70">
                    <input type="checkbox" className="mt-1" checked={form.consentPrivacy} onChange={(e) => setForm({ ...form, consentPrivacy: e.target.checked })} required />
                    <span>I agree to the Privacy Policy and give my Consent.</span>
                  </label>
                  {ownPhotosMode && (
                    <label className="flex items-start gap-3 text-sm text-[#F7F1E8]/70">
                      <input type="checkbox" className="mt-1" checked={form.consentFaceSearch} onChange={(e) => setForm({ ...form, consentFaceSearch: e.target.checked })} required />
                      <span>I consent to use my selfie video only to find my photos in this private event gallery.</span>
                    </label>
                  )}
                  <label className="flex items-start gap-3 text-sm text-[#F7F1E8]/55">
                    <input type="checkbox" className="mt-1" checked={form.consentMarketing} onChange={(e) => setForm({ ...form, consentMarketing: e.target.checked })} />
                    <span>I would like to receive future promotions/offers.</span>
                  </label>
                </>
              )}

              {error && <div className="border border-red-500/30 text-red-200 p-3 text-sm">{error}</div>}

              <button disabled={submitting || (ownPhotosMode && selfieFrames.length < 3)} className="btn-gold-fill w-full flex items-center justify-center gap-2">
                {submitting ? <LoaderCircle className="animate-spin" size={17} /> : <CheckCircle2 size={17} />}
                {submitting && ownPhotosMode ? "Finding your photos..." : requiresRegistration ? "Register & Open Gallery" : "Open Gallery"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const SELFIE_STEPS = [
  "Look straight",
  "Turn slightly left",
  "Turn slightly right",
  "Hold still",
];

function GuidedSelfieCapture({ frames, onFrames }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [cameraError, setCameraError] = useState("");

  useEffect(() => () => stopCamera(), []);

  const stopCamera = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const startCamera = async () => {
    setCameraError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 1280, min: 720 },
          height: { ideal: 720, min: 540 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch (error) {
      setCameraError("Camera permission denied or unavailable. Upload clear selfie images instead.");
      setCameraReady(false);
    }
  };

  const captureFrame = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) return null;
    const canvas = document.createElement("canvas");
    const scale = Math.max(720 / video.videoWidth, 1);
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.9);
  };

  const captureGuidedBurst = async () => {
    if (!cameraReady) await startCamera();
    setCapturing(true);
    setStepIndex(0);
    onFrames([]);
    const captured = [];
    let tick = 0;
    timerRef.current = window.setInterval(async () => {
      tick += 1;
      setStepIndex(Math.min(SELFIE_STEPS.length - 1, Math.floor((tick - 1) / 3)));
      const frame = await captureFrame();
      if (frame) captured.push(frame);
      if (tick >= 14) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
        const selected = pickRepresentativeFrames(captured);
        onFrames(selected);
        setCapturing(false);
        stopCamera();
        setCameraReady(false);
      }
    }, 320);
  };

  const uploadFallback = async (event) => {
    const files = [...(event.target.files || [])].slice(0, 5);
    const payloads = await Promise.all(files.map(fileToDataUrl));
    onFrames(payloads.filter(Boolean));
  };

  return (
    <div className="border border-[#C99A45]/15 p-4">
      <div className="flex items-start gap-3">
        <Camera className="text-[#C99A45] shrink-0" size={18} />
        <div>
          <div className="text-[#F7F1E8]">Live Selfie Video Capture</div>
          <p className="text-sm text-[#F7F1E8]/55 mt-1">
            Capture once. We extract multiple clear frames: straight, left, right, and still.
          </p>
        </div>
      </div>

      <div className="mt-4 grid lg:grid-cols-[280px_1fr] gap-4">
        <div className="relative aspect-[3/4] bg-black border border-white/10 overflow-hidden flex items-center justify-center">
          <video ref={videoRef} playsInline muted className={`w-full h-full object-cover ${cameraReady ? "block" : "hidden"}`} />
          {!cameraReady && <div className="text-center text-xs text-[#F7F1E8]/45 px-5">Camera preview will appear here</div>}
          {capturing && (
            <div className="absolute inset-x-0 bottom-0 bg-black/70 p-3 text-center">
              <div className="eyebrow text-[#C99A45]">{SELFIE_STEPS[stepIndex]}</div>
              <div className="h-1 bg-white/10 mt-2">
                <div className="h-full bg-[#C99A45] transition-all" style={{ width: `${((stepIndex + 1) / SELFIE_STEPS.length) * 100}%` }} />
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="grid grid-cols-2 gap-2 text-xs text-[#F7F1E8]/60">
            {SELFIE_STEPS.map((step, index) => (
              <div key={step} className={`border p-3 ${index === stepIndex && capturing ? "border-[#C99A45] text-[#F7F1E8]" : "border-white/10"}`}>
                Step {index + 1}: {step}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {!cameraReady && <button type="button" onClick={startCamera} className="btn-gold-outline">Start Camera</button>}
            <button type="button" onClick={captureGuidedBurst} disabled={capturing} className="btn-gold-fill">
              {capturing ? "Capturing..." : frames.length ? "Retake Selfie" : "Capture Selfie"}
            </button>
            <label className="btn-gold-outline cursor-pointer">
              Upload fallback
              <input type="file" accept="image/*" multiple className="hidden" onChange={uploadFallback} />
            </label>
          </div>
          {cameraError && <p className="text-red-200 text-xs mt-3">{cameraError}</p>}
          <p className="text-[11px] text-[#F7F1E8]/40 mt-3">Target frame width is at least 720px. Frames are sent privately to the backend; embeddings are never exposed to the browser.</p>

          {frames.length > 0 && (
            <div className="mt-4">
              <div className="eyebrow text-[9px] text-[#C99A45] mb-2">{frames.length} selfie frames ready</div>
              <div className="grid grid-cols-5 gap-2">
                {frames.map((frame, index) => (
                  <img key={`${frame.slice(0, 40)}-${index}`} src={frame} alt={`Selfie frame ${index + 1}`} className="aspect-square object-cover border border-white/10" />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function pickRepresentativeFrames(frames) {
  if (frames.length <= 5) return frames;
  const picks = [
    frames[1],
    frames[Math.floor(frames.length * 0.35)],
    frames[Math.floor(frames.length * 0.62)],
    frames[Math.floor(frames.length * 0.82)],
    frames[frames.length - 2],
  ].filter(Boolean);
  return [...new Set(picks)].slice(0, 5);
}

function fileToDataUrl(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => resolve("");
    reader.readAsDataURL(file);
  });
}
